import {
  getCardNetworkStringFromNiceType,
  removeNonNumber,
} from "cc-tools/helpers";

import { PayRequestPayload } from "checkout-query-client/api/types";
import { MerchantSettings } from "checkout-query-client/hooks/use-merchant-settings";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import { PaymentType } from "checkout-query-client/types/form-types";
import { buildBillingAddress } from "checkout-query-client/utils/build-payloads/build-billing-address";
import {
  getAcceptedMandates,
  getDeviceFingerprintSessionId,
  getExpirationMonthYear,
} from "checkout-query-client/utils/helpers";

import { IEncryptData } from "encrypt-value/encrypt";
import {
  createEncryptedValueGetter,
  isValueEncrypted,
} from "encrypt-value/tools";

import {
  BrowserInfo,
  CardNetwork,
  EncryptedCard,
  EncryptedCardHolderName,
  PayRequest,
  PayResponse_Status,
  PaymentMethod,
  PaymentMethod_Type,
  PreferredRoutingInitiator,
} from "frontend-client";

export const buildCreditCardPayload = async (
  payload: PayRequestPayload
): Promise<PayRequest> => {
  const { formFields, merchantSettings, orderDigest } = payload;
  const request = new PayRequest();
  const encryptedCard = new EncryptedCard();
  const paymentMethod = new PaymentMethod();
  const browserInfo = new BrowserInfo();

  if (formFields && merchantSettings) {
    // Logic around Billing address
    const {
      billingAddressSameAsShipping,
      billingAddress,
      billingAddressFromIntentBilling,
    } = buildBillingAddress(payload, PaymentType.creditCard);
    encryptedCard.billingAddress = billingAddress;
    encryptedCard.billingAddressSameAsShipping = billingAddressSameAsShipping;
    request.billingAddressFromIntentBilling = billingAddressFromIntentBilling;

    const creditCardPayload = await getCreditCardPayload(
      merchantSettings,
      formFields
    );

    if (
      creditCardPayload.status === PayResponse_Status.SUCCESS &&
      creditCardPayload.card
    ) {
      encryptedCard.encryptedCardHolderName = new EncryptedCardHolderName({
        encryptedFullName: creditCardPayload.card.cardHolderName,
      });
      encryptedCard.encryptedCvv = creditCardPayload.card.encryptedCvv;
      encryptedCard.encryptedPan = creditCardPayload.card.encryptedPan;
      encryptedCard.encryptionKeyId = creditCardPayload.card.encryptionKeyId;
      encryptedCard.encryptedExpirationMonth =
        creditCardPayload.card.encryptedExpirationMonth;
      encryptedCard.encryptedExpirationYear =
        creditCardPayload.card.encryptedExpirationYear;
      encryptedCard.preferredPaymentNetworkRouting =
        creditCardPayload.card.preferredPaymentNetworkRouting;
      encryptedCard.preferredRoutingInitiator =
        encryptedCard.preferredPaymentNetworkRouting
          ? PreferredRoutingInitiator.CONSUMER
          : PreferredRoutingInitiator.UNSPECIFIED;
    }

    paymentMethod.card = encryptedCard;
    paymentMethod.type = PaymentMethod_Type.CARD;
    request.paymentMethod = paymentMethod;
    request.acceptedMandates = getAcceptedMandates(formFields);

    // 3ds TODO: tie this in
    request.scaChallengeWindowSize = 4;
    // text user consent "agreed" too
    request.consentToStoreForFuturePayments =
      formFields?.collectConsentToStoreForFuturePayments === "true"
        ? (formFields?.collectConsentToStoreForFuturePaymentsText ?? "")
        : "";
    // setting payment method to default mop
    request.makeDefaultPaymentMethod = formFields?.setAsDefault === "true";
    // saving consumer profile based on the merchant intent.
    request.savePaymentMethodToProfile =
      formFields?.collectConsentToStoreForFuturePayments === "true";

    const date = new Date();
    browserInfo.acceptHeader = "text/html;charset=UTF-8";
    browserInfo.language = window.navigator.language || "";
    browserInfo.colorDepth = window.screen.colorDepth || 0;
    browserInfo.screenHeight = window.screen.height || 0;
    browserInfo.screenWidth = window.screen.width || 0;
    browserInfo.timezoneOffset = date.getTimezoneOffset() || 0;
    request.browserInfo = browserInfo;
    request.orderDigest = orderDigest ?? "";
    request.deviceFingerprintSessionId = getDeviceFingerprintSessionId();
  }
  return request;
};

export const getExpirationFields = (formFields: Record<string, string>) => {
  let expirationMonth = formFields?.expirationMonth;
  let expirationYear = formFields?.expirationYear;

  if (formFields?.expiration) {
    if (isValueEncrypted(formFields?.expiration)) {
      const expirationEncParts = formFields?.expiration.split(" ");
      expirationMonth = expirationEncParts[0];
      expirationYear = expirationEncParts[1];
    } else {
      // no encrypted get values from expiration field
      const expiration = getExpirationMonthYear(formFields?.expiration);
      expirationMonth = expiration.expirationMonth.toString();
      expirationYear = expiration.expirationYear.toString();
    }
  }

  return [expirationMonth, expirationYear];
};

const getCreditCardPayload = async (
  merchantSettings: MerchantSettings,
  formFields: Record<string, string>
) => {
  let expirationMonth: string = "";
  let expirationYear: string = "";
  if (
    merchantSettings.paymentMethods?.cardPaymentMethod?.expiryDateRequired ||
    formFields.expiration ||
    formFields.expirationMonth ||
    formFields.expirationYear
  ) {
    [expirationMonth, expirationYear] = getExpirationFields(formFields);
  }

  let encryptedPanData: IEncryptData;
  let encryptedSecurityCodeData: IEncryptData;
  let encryptedCardHolderNameData: IEncryptData;
  let encryptedExpirationMonth: IEncryptData;
  let encryptedExpirationYear: IEncryptData;
  let preferredPaymentNetworkRouting;
  let preferredRoutingInitiator;
  const getEncryptedValue = createEncryptedValueGetter(
    merchantSettings?.encryptionKey
  );
  try {
    encryptedPanData = await getEncryptedValue(
      isValueEncrypted(formFields?.cardNumber ?? "")
        ? formFields?.cardNumber
        : removeNonNumber(formFields?.cardNumber ?? "")
    );
    encryptedSecurityCodeData = await getEncryptedValue(
      formFields?.securityCode ?? ""
    );
    encryptedCardHolderNameData = await getEncryptedValue(
      formFields?.nameOnCard ?? formFields?.billingAddressName ?? ""
    );
    encryptedExpirationMonth = await getEncryptedValue(expirationMonth);
    encryptedExpirationYear = await getEncryptedValue(expirationYear);
    if (formFields?.selectNetwork) {
      const cardNetworkString = getCardNetworkStringFromNiceType(
        formFields?.selectNetwork
      );
      preferredPaymentNetworkRouting = CardNetwork[
        cardNetworkString as unknown as CardNetwork
      ] as unknown as CardNetwork;
      preferredRoutingInitiator = CardNetwork[
        cardNetworkString as unknown as CardNetwork
      ] as unknown as CardNetwork;
    }
    return {
      status: PayResponse_Status.SUCCESS,
      card: {
        cardHolderName: encryptedCardHolderNameData.encryptedData ?? "",
        encryptedCvv: encryptedSecurityCodeData.encryptedData,
        encryptedPan: encryptedPanData.encryptedData,
        encryptionKeyId: merchantSettings?.encryptionKeyId ?? "",
        encryptedExpirationMonth: encryptedExpirationMonth.encryptedData ?? "",
        encryptedExpirationYear: encryptedExpirationYear.encryptedData ?? "",
        preferredPaymentNetworkRouting,
        preferredRoutingInitiator,
      },
    };
  } catch (e) {
    usePubSub().publish({
      level: "error",
      namespace: PubSubNamespace.payment,
      message: PubSubMessage.CreditCardEncryptionError,
    });
    return { status: PayResponse_Status.UNSPECIFIED };
  }
};
